import { FunctionComponent, ChangeEvent } from 'react';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import { connect } from 'react-redux';
import CheckoutRadioContainer from '../../shared/CheckoutRadioContainer';
import CheckoutBankAccountView from './CheckoutBankAccountView';
import CheckoutACHForm from '../CheckoutACHForm';
import classNames from 'classnames';

import { isVerifyingBankAccount } from '../../reducers/checkoutPaymentReducer';
import { PANES, ACTIONS } from '../../constants/checkoutConstants';

import { CheckoutBankAccount_viewer$data } from './__generated__/CheckoutBankAccount_viewer.graphql';
import { CheckoutBankAccount_buyer$data } from './__generated__/CheckoutBankAccount_buyer.graphql';
import { CheckoutBankAccount_transaction$data } from './__generated__/CheckoutBankAccount_transaction.graphql';

import styles from '../creditCard/styles/CheckoutCreditCard.scss';

export type ActionsType = {
    onCancel?: () => void;
    onDelete?: (arg1: string) => void;
};

export type OnSelectEventType = (arg1: ChangeEvent, arg2: string, arg3?: boolean) => void;

export type CheckoutPaymentErrors = ObjectType;
export type ValidationUpdate = () => void;
export type ValidationError = () => void;

type Props = {
    viewer: CheckoutBankAccount_viewer$data;
    buyer: CheckoutBankAccount_buyer$data;
    transaction: CheckoutBankAccount_transaction$data;
    actions: ActionsType;
    bankAccountToken: string;
    isSelected?: boolean;
    isVerifying?: boolean;
    onSelect: OnSelectEventType;
    checkoutPaymentErrors?: CheckoutPaymentErrors;
    onValidationUpdate?: ValidationUpdate;
    onValidationError?: ValidationError;
};

const CheckoutBankAccount: FunctionComponent<Props> = ({
    viewer,
    buyer,
    actions,
    bankAccountToken,
    isSelected = false,
    isVerifying = false,
    onSelect,
    transaction,
}) => {
    const walletPaymentMethods = buyer?.wallet?.paymentMethods || [];
    let accountHolderName: string | null = null;
    const walletPaymentMethod =
        walletPaymentMethods.find(({ paymentMethod }: $TSFixMe) => {
            accountHolderName = paymentMethod.accountHolderName;
            return paymentMethod && paymentMethod.token === bankAccountToken;
        }) || null;
    const bankAccount = walletPaymentMethod?.paymentMethod;

    if (!bankAccount) {
        throw new Error('Bank Account Not Found');
    }

    const isMobile = false;

    const props = {
        type: PANES.PANE_PAYMENT,
        serviceId: bankAccountToken,
        isSelected,
        isClickable: !isSelected,
        actions: {},
        label: <CheckoutBankAccountView bankAccount={bankAccount} transaction={transaction} />,
        wrapperClass: classNames(styles.radioWrapper, { [styles.isMobile]: isMobile }),
        actionsWrapperClass: styles.actionsWrapper,
        onSelect,
    };

    const isVerifyingForm = isVerifying && isSelected;

    if (isVerifyingForm) {
        Object.assign(props, {
            label: accountHolderName,
            isClickable: !isSelected,
            actions: {
                [ACTIONS.ACTION_CANCEL]: actions.onCancel,
                [ACTIONS.ACTION_DELETE]: actions.onDelete,
            },
            onSelect: (value: string, event: ChangeEvent) => onSelect(event, value, isSelected),
        });
    } else {
        Object.assign(props, {
            label: (
                <CheckoutBankAccountView
                    bankAccount={bankAccount}
                    showTitle={isMobile}
                    transaction={transaction}
                />
            ),
            isClickable: !isSelected,
            actions: {
                [ACTIONS.ACTION_DELETE]: actions.onDelete,
            },
            onSelect: (value: string, event: ChangeEvent) => onSelect(event, value, isSelected),
        });
    }

    return (
        <CheckoutRadioContainer {...props}>
            {isVerifyingForm && <CheckoutACHForm viewer={viewer} buyer={buyer} />}
        </CheckoutRadioContainer>
    );
};

type ConnectPaymentType = {
    isVerifyingBankAccount: boolean;
};

type ConnectType = {
    payment: ConnectPaymentType;
};

const ConnectedCheckoutBankAccount = connect(({ payment }: ConnectType) => ({
    isVerifying: isVerifyingBankAccount(payment),
}))(CheckoutBankAccount);

export default createFragmentContainer(ConnectedCheckoutBankAccount, {
    viewer: graphql`
        fragment CheckoutBankAccount_viewer on Viewer {
            ...CheckoutACHForm_viewer
        }
    `,
    buyer: graphql`
        fragment CheckoutBankAccount_buyer on User {
            ...CheckoutACHForm_buyer
            wallet {
                paymentMethods {
                    paymentMethod {
                        ...CheckoutBankAccountView_bankAccount
                        __typename
                        ... on ACH {
                            display
                            accountType
                            accountHolderName
                            last4
                            token
                        }
                    }
                    shippingAddresses {
                        serviceId
                    }
                }
            }
        }
    `,
    transaction: graphql`
        fragment CheckoutBankAccount_transaction on Transaction {
            ...CheckoutBankAccountView_transaction
        }
    `,
});
